/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-core: turbulent-energy model inference (core-viewer.js 3390-3503).
// Walks the serialized decision-tree forest / gradient-boosting model that the
// prototype received as __TURBULENT_MODEL__ (now an explicit `turbulentModel`
// argument, to be served alongside the guide payload). Falls back to the
// hardcoded logistic model when no serialized model is supplied.

// Hardcoded logistic fallback (CirCor audio-only features) — verbatim from the prototype.
export const TURBULENT_LOGISTIC_FALLBACK = {
  model: "logistic_regression_audio_features",
  source: "CirCor audio-only turbulent-energy features, edge trim 1s",
  features: ["n_cycles", "sys_cycle_pct", "dia_cycle_pct", "sys_mean", "dia_mean", "sys_peak", "dia_peak"],
  mean: [
    31.878200692041524, 27.432745570934244, 29.745169031141817, 0.03709570934256059, 0.03130314878892734,
    0.5090979238754334, 0.5281420069204157,
  ],
  scale: [
    14.032551463248494, 34.09047094314352, 34.899151315571984, 0.039810119328726225, 0.022832997743022696,
    0.28578021189589115, 0.28917425116433915,
  ],
  coef: [
    0.11287575601508446, -0.6331783400955222, 0.3655369018184988, 2.746699583698781, -0.6498154150573716,
    0.06392460042458013, -0.13966089141223445,
  ],
  intercept: -0.11495716095782992,
  decisionThreshold: 0.54,
}

export function turbulentModelName(turbulentModel) {
  return turbulentModel && turbulentModel.version ? turbulentModel.version : TURBULENT_LOGISTIC_FALLBACK.model
}

export function turbulentModelSource(turbulentModel) {
  return turbulentModel && turbulentModel.source ? turbulentModel.source : TURBULENT_LOGISTIC_FALLBACK.source
}

export function turbulentDecisionThreshold(turbulentModel) {
  return turbulentModel && typeof turbulentModel.decision_threshold === "number"
    ? turbulentModel.decision_threshold
    : TURBULENT_LOGISTIC_FALLBACK.decisionThreshold
}

export function predictTreeProbability(tree, features) {
  let node = 0
  const left = tree.children_left
  const right = tree.children_right
  const feat = tree.feature
  const threshold = tree.threshold
  while (left[node] !== -1 && right[node] !== -1) {
    node = features[feat[node]] <= threshold[node] ? left[node] : right[node]
  }
  const value = tree.value[node] || [1, 0]
  const total = (value[0] || 0) + (value[1] || 0)
  return total > 0 ? (value[1] || 0) / total : 0
}

export function predictBoostingValue(tree, features) {
  let node = 0
  const left = tree.children_left
  const right = tree.children_right
  const feat = tree.feature
  const threshold = tree.threshold
  while (left[node] !== -1 && right[node] !== -1) {
    node = features[feat[node]] <= threshold[node] ? left[node] : right[node]
  }
  return tree.value[node] || 0
}

export function turbulentFeatureMap(baseFeatures) {
  const raw = Array.isArray(baseFeatures)
    ? {
        n_cycles: baseFeatures[0],
        sys_cycle_pct: baseFeatures[1],
        dia_cycle_pct: baseFeatures[2],
        sys_mean: baseFeatures[3],
        dia_mean: baseFeatures[4],
        sys_peak: baseFeatures[5],
        dia_peak: baseFeatures[6],
      }
    : { ...baseFeatures }
  const nCycles = raw.n_cycles || 0
  const sysCyclePct = raw.sys_cycle_pct || 0
  const diaCyclePct = raw.dia_cycle_pct || 0
  const sysMean = raw.sys_mean || 0
  const diaMean = raw.dia_mean || 0
  const sysPeak = raw.sys_peak || 0
  const diaPeak = raw.dia_peak || 0
  return {
    ...raw,
    n_cycles: nCycles,
    sys_cycle_pct: sysCyclePct,
    dia_cycle_pct: diaCyclePct,
    sys_mean: sysMean,
    dia_mean: diaMean,
    sys_peak: sysPeak,
    dia_peak: diaPeak,
    max_cycle_pct: Math.max(sysCyclePct, diaCyclePct),
    cycle_pct_delta: Math.abs(sysCyclePct - diaCyclePct),
    min_cycle_pct: Math.min(sysCyclePct, diaCyclePct),
    sum_cycle_pct: sysCyclePct + diaCyclePct,
    max_mean: Math.max(sysMean, diaMean),
    mean_delta: Math.abs(sysMean - diaMean),
    max_peak: Math.max(sysPeak, diaPeak),
    peak_delta: Math.abs(sysPeak - diaPeak),
    peak_ratio_sys_dia: sysPeak / (diaPeak + 1e-6),
    mean_ratio_sys_dia: sysMean / (diaMean + 1e-6),
    avg_peak: 0.5 * (sysPeak + diaPeak),
    avg_mean: 0.5 * (sysMean + diaMean),
  }
}

export function turbulentFeatureVector(turbulentModel, baseFeatures) {
  const fmap = turbulentFeatureMap(baseFeatures)
  const columns = turbulentModel && turbulentModel.feature_columns
  if (columns && columns.length) {
    return columns.map((col) => fmap[col] ?? 0)
  }
  return baseFeatures
}

export function predictTurbulentScore(turbulentModel, baseFeatures) {
  if (turbulentModel && turbulentModel.trees && turbulentModel.trees.length) {
    const features = turbulentFeatureVector(turbulentModel, baseFeatures)
    if (turbulentModel.model_type === "gradient_boosting_classifier") {
      const raw = turbulentModel.trees.reduce((acc, tree) => {
        return acc + (turbulentModel.learning_rate || 0.04) * predictBoostingValue(tree, features)
      }, turbulentModel.base_log_odds || 0)
      return 1 / (1 + Math.exp(-raw))
    }
    const sum = turbulentModel.trees.reduce((acc, tree) => acc + predictTreeProbability(tree, features), 0)
    return sum / turbulentModel.trees.length
  }
  // Logistic fallback expects the 7-feature ordered vector (array form).
  const vec = Array.isArray(baseFeatures)
    ? baseFeatures
    : TURBULENT_LOGISTIC_FALLBACK.features.map((name) => baseFeatures[name] || 0)
  const z =
    TURBULENT_LOGISTIC_FALLBACK.intercept +
    vec.reduce((acc, v, i) => {
      return (
        acc +
        ((v - TURBULENT_LOGISTIC_FALLBACK.mean[i]) / (TURBULENT_LOGISTIC_FALLBACK.scale[i] || 1)) *
          TURBULENT_LOGISTIC_FALLBACK.coef[i]
      )
    }, 0)
  return 1 / (1 + Math.exp(-z))
}
